import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  VStack,
  Text,
  Button,
  Flex,
  HStack,
  Textarea,
  Switch,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { FiX, FiMic, FiCheck } from "react-icons/fi";
import useSpeechToText from "@hooks/useSpeechToText";
import RemixPollIcon from "@components/Icons/RemixPollIcon";
import RemixQuizIcon from "@components/Icons/RemixQuizIcon";
import RemixAnnouncementIcon from "@components/Icons/RemixAnnouncementIcon";

// Get the type label, color, and icon component for activity types
const getActivityTypeConfig = (activity) => {
  const type = activity?.type;
  switch (type) {
    case "poll":
      return { label: "Poll:", color: "#E5A100", Icon: RemixPollIcon };
    case "quiz":
      return { label: "Quiz:", color: "#5DCDFD", Icon: RemixQuizIcon };
    case "share":
      return activity?.link
        ? {
            label: "Link Share:",
            color: "#32D583",
            Icon: RemixAnnouncementIcon,
          }
        : {
            label: "Announcement:",
            color: "#4C47CC",
            Icon: RemixAnnouncementIcon,
          };
    default:
      return {
        label: "Announcement:",
        color: "#4C47CC",
        Icon: RemixAnnouncementIcon,
      };
  }
};

/**
 * BaseRemixModal - Shared component for remixing activities and agendas
 * Contains all common functionality including speech-to-text
 */
const BaseRemixModal = ({
  isOpen,
  entity, // The activity or agenda object
  entityType, // "activity" or "agenda"
  onClose,
  onRemixComplete,
  isLoading = false,
  // Customizable props
  title = "What would you like to refine?",
  switchLabel,
  buttonText = "\u2728 Remix",
}) => {
  const [hostRefinements, setHostRefinements] = useState("");
  const [keepSameType, setKeepSameType] = useState(true);
  const [toggleChanged, setToggleChanged] = useState(false);
  const [textBeforeRecording, setTextBeforeRecording] = useState("");

  // Speech to text hook
  const {
    isRecording,
    transcript,
    recordingDuration,
    error: speechError,
    isSupported,
    startRecording,
    stopRecording,
    cancelRecording,
    resetTranscript,
    formatTime,
  } = useSpeechToText();

  // Helper function to capitalize transcript intelligently
  const capitalizeTranscript = (transcript, previousText) => {
    if (!transcript) return transcript;

    // Check if we need to capitalize (start of text or after sentence-ending punctuation)
    const needsCapital =
      !previousText ||
      previousText.trim() === "" ||
      /[.!?]\s*$/.test(previousText);

    if (needsCapital) {
      return transcript.charAt(0).toUpperCase() + transcript.slice(1);
    }

    return transcript;
  };

  // Reset transient inputs whenever the modal opens or the target entity changes,
  // so refinements from a previous remix don't leak into the next activity.
  useEffect(() => {
    if (isOpen) {
      setHostRefinements("");
      setKeepSameType(true);
      setToggleChanged(false);
      setTextBeforeRecording("");
    }
  }, [isOpen, entity?._id]);

  // Update hostRefinements when transcript changes during recording
  useEffect(() => {
    if (transcript && isRecording) {
      // Apply smart capitalization to the transcript
      const processedTranscript = capitalizeTranscript(
        transcript,
        textBeforeRecording
      );

      // Combine the text before recording with the new transcript
      const combinedText =
        textBeforeRecording +
        (textBeforeRecording && !textBeforeRecording.endsWith(" ") ? " " : "") +
        processedTranscript;

      setHostRefinements(combinedText);
    }
  }, [transcript, isRecording, textBeforeRecording]);

  const handleStartRecording = () => {
    // Save the current text before starting recording
    setTextBeforeRecording(hostRefinements);
    // Don't reset transcript here - let the hook handle it
    startRecording();
  };

  const handleStopRecording = () => {
    // Just stop recording, keep the accumulated text
    stopRecording();
    resetTranscript();
  };

  const handleCancelRecording = () => {
    // Restore the text from before recording started
    setHostRefinements(textBeforeRecording);
    cancelRecording();
    resetTranscript();
  };

  const handleRemixComplete = () => {
    const remixData = {
      [entityType]: entity,
      hostRefinements,
      // For agenda remix, send keepActivityTypes toggle value
      // For activity remix, the AI decides based on the user's refinement text
      ...(entityType === "agenda" && { keepActivityTypes: keepSameType }),
    };
    onRemixComplete?.(remixData);
  };

  // Activity-specific header with type icon, label, title, and options
  const renderActivityHeader = () => {
    if (entityType !== "activity" || !entity) return null;

    const typeConfig = getActivityTypeConfig(entity);
    const TypeIcon = typeConfig.Icon;
    const options = entity.options || [];
    const type = entity.type;
    const correctIndex = entity?.settings?.correctAnswerIndex;

    return (
      <VStack spacing={3} align="stretch">
        {/* Type icon pill + close button */}
        <Flex justify="space-between" align="flex-start">
          <Box>
            <TypeIcon />
          </Box>
          <IconButton
            icon={<FiX />}
            size="sm"
            variant="ghost"
            color="#0C001C"
            onClick={onClose}
            data-testid="close-button"
          />
        </Flex>

        {/* Type label */}
        <Text
          fontSize="20px"
          fontWeight="700"
          lineHeight="1.33"
          letterSpacing="-0.04em"
          color={typeConfig.color}
          fontFamily="Geist, sans-serif"
        >
          {typeConfig.label}
        </Text>

        {/* Activity title (share without link uses content as title) */}
        {(type === "share" && !entity.link
          ? entity.content || entity.title
          : entity.title) && (
          <Text
            fontSize="20px"
            fontWeight="700"
            lineHeight="1.33"
            letterSpacing="-0.04em"
            color="#0C001C"
            fontFamily="Geist, sans-serif"
          >
            {type === "share" && !entity.link
              ? entity.content || entity.title
              : entity.title}
          </Text>
        )}

        {/* Activity options for polls and quizzes */}
        {options.length > 0 && (type === "poll" || type === "quiz") && (
          <VStack spacing={1} align="stretch">
            {options.map((option, index) => {
              const isCorrect = type === "quiz" && index === correctIndex;
              const optionText =
                option?.option || option?.text || `Option ${index + 1}`;
              return (
                <Text
                  key={option._id || index}
                  fontSize="13px"
                  fontWeight={isCorrect ? 600 : 400}
                  color={isCorrect ? "#00B894" : "#433358"}
                  fontFamily="Geist, sans-serif"
                >
                  {optionText}
                </Text>
              );
            })}
          </VStack>
        )}
      </VStack>
    );
  };

  // Agenda header (existing behavior)
  const renderAgendaHeader = () => {
    if (entityType === "activity") return null;

    return (
      <Flex justify="space-between" align="flex-start">
        <Box flex={1}>
          <Text
            fontSize="24px"
            fontWeight="700"
            lineHeight="1.33"
            letterSpacing="-0.04em"
            color="#0C001C"
            textAlign="left"
            fontFamily="Geist, sans-serif"
            textTransform="capitalize"
          >
            {title}
          </Text>
        </Box>
        <IconButton
          icon={<FiX />}
          size="sm"
          variant="ghost"
          color="#0C001C"
          onClick={onClose}
          data-testid="close-button"
          ml={2}
        />
      </Flex>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      size="sm"
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="linear-gradient(180deg, #DBDAF5 0%, #F8F8FF 10%, white 100%)"
        borderRadius="32px 32px 0 0"
        maxW="425px"
        mx="16px"
        mb={0}
        mt="auto"
        position="relative"
        overflow="visible"
      >
        <ModalBody p="32px 16px 16px">
          <VStack spacing={8} align="stretch">
            {/* Header - differs between activity and agenda */}
            {entityType === "activity"
              ? renderActivityHeader()
              : renderAgendaHeader()}

            {/* Main Content */}
            <VStack spacing={5} align="stretch">
              {/* Adjustment Text Area */}
              <VStack spacing={2} align="stretch">
                <Text
                  fontSize="16px"
                  fontWeight="400"
                  lineHeight="1.5"
                  letterSpacing="-0.02em"
                  fontFamily="Geist, sans-serif"
                >
                  What do you want to adjust or emphasize?
                </Text>
                <Box
                  position="relative"
                  border="1px solid #D0CCD5"
                  borderRadius="12px"
                >
                  <Textarea
                    value={hostRefinements}
                    onChange={(e) => setHostRefinements(e.target.value)}
                    placeholder="• Make question more challenging
• Add more answer choices and emoji
• Integrate content from attached image"
                    bg="#fcfaff"
                    border="none"
                    minH="88px"
                    fontSize="14px"
                    fontWeight="400"
                    lineHeight="1.43"
                    letterSpacing="-0.02em"
                    color="#0C001C"
                    fontFamily="Geist, sans-serif"
                    resize="none"
                    p={3}
                    _placeholder={{
                      color: "#A199AB",
                    }}
                    _hover={{
                      borderColor: "none",
                    }}
                    _focus={{
                      borderColor: "none",
                      boxShadow: "none",
                    }}
                    data-testid="adjustment-textarea"
                  />
                  <Box w="100%" padding="2px 10px">
                    {!isRecording ? (
                      <HStack spacing={2} justifyContent="end">
                        <Tooltip
                          label={
                            !isSupported
                              ? "Speech recognition not supported in this browser"
                              : speechError || "Click to start voice input"
                          }
                          placement="top"
                        >
                          <IconButton
                            icon={<FiMic />}
                            size="md"
                            variant="ghost"
                            color={!isSupported ? "#D0CCD5" : "#A199AB"}
                            isDisabled={!isSupported}
                            onClick={handleStartRecording}
                            _hover={{
                              color: !isSupported ? "#D0CCD5" : "#4C47CC",
                              bg: !isSupported ? "transparent" : "gray.100",
                            }}
                            data-testid="start-recording-button"
                          />
                        </Tooltip>
                      </HStack>
                    ) : (
                      <Flex align="center" justify="space-between">
                        {/* Cancel X on the left */}
                        <Tooltip label="Cancel recording" placement="top">
                          <IconButton
                            icon={<FiX />}
                            size="sm"
                            variant="ghost"
                            color="#A199AB"
                            onClick={handleCancelRecording}
                            _hover={{
                              color: "#6B7280",
                              bg: "gray.100",
                            }}
                            data-testid="cancel-recording-button"
                          />
                        </Tooltip>

                        {/* Timer in the center */}
                        <Text
                          fontSize="12px"
                          fontWeight="400"
                          color="#0C001C"
                          fontFamily="Geist, sans-serif"
                          letterSpacing="-0.01em"
                        >
                          {formatTime(recordingDuration)}
                        </Text>

                        {/* Check circle on the right */}
                        <Tooltip label="Save recording" placement="top">
                          <Box
                            as="button"
                            onClick={handleStopRecording}
                            w="24px"
                            h="24px"
                            borderRadius="full"
                            bg="#2E2B7A"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            _hover={{
                              bg: "#4C47CC",
                            }}
                            transition="background 0.2s"
                            data-testid="stop-recording-button"
                          >
                            <FiCheck size="16px" color="white" />
                          </Box>
                        </Tooltip>
                      </Flex>
                    )}
                  </Box>
                </Box>
              </VStack>
            </VStack>

            {/* Keep Activity Types Toggle - only shown for agenda remix */}
            {entityType === "agenda" && (
              <VStack spacing={3} align="stretch">
                <Flex justify="space-between" align="center">
                  <Text
                    fontSize="11px"
                    fontWeight="400"
                    lineHeight="1.45"
                    letterSpacing="-0.01em"
                    color="#0C001C"
                    fontFamily="Geist, sans-serif"
                  >
                    {switchLabel || "Keep same agenda types"}
                  </Text>
                  <Switch
                    isChecked={keepSameType}
                    onChange={(e) => {
                      setKeepSameType(e.target.checked);
                      setToggleChanged(true);
                    }}
                    colorScheme="purple"
                    size="sm"
                    data-testid="activity-type-switch"
                  />
                </Flex>
              </VStack>
            )}

            {/* Button Section */}
            <VStack spacing={0} align="stretch">
              <Button
                bg="#4C47CC"
                color="white"
                fontFamily="Geist, sans-serif"
                fontWeight="700"
                fontSize="13px"
                lineHeight="1.23"
                h="auto"
                py={4}
                px="55px"
                borderRadius="8px"
                isLoading={isLoading}
                loadingText="Remixing..."
                isDisabled={
                  entityType === "agenda"
                    ? !hostRefinements.trim() && !toggleChanged
                    : !hostRefinements.trim()
                }
                onClick={handleRemixComplete}
                _hover={{
                  bg: "#3D37B8",
                }}
                _active={{
                  bg: "#332A9F",
                }}
                data-testid="remix-complete-button"
              >
                {buttonText}
              </Button>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BaseRemixModal;
